import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  LoadingState,
  Modal,
  StatePanel,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2DataGrid,
  V2FormField,
  V2FormGrid,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { ApiError, apiPatch, apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useCapabilities } from '../../lib/capabilities.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import type { AccountingBook, AccountingBookListResponse } from '../../lib/types/finance.js';
import { isStatutoryBook, refreshFinanceBooks } from './useFinanceBook.js';

const KIND_LABEL: Readonly<Record<AccountingBook['kind'], string>> = {
  base: 'پایه',
  statutory: 'قانونی',
  management: 'مدیریتی',
  tax: 'مالیاتی',
  consolidation: 'تجمیعی',
};

const STATUS_LABEL: Readonly<Record<AccountingBook['status'], string>> = {
  active: 'فعال',
  closed: 'غیرفعال',
  archived: 'بایگانی',
};

const NEW_BOOK_KINDS: readonly { value: AccountingBook['kind']; label: string }[] = [
  { value: 'management', label: 'مدیریتی' },
  { value: 'tax', label: 'مالیاتی' },
  { value: 'consolidation', label: 'تجمیعی' },
];

function errorText(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return 'شما اجازهٔ مدیریت دفترها را ندارید.';
    if (error.status === 409) return 'دفتری با این کد از قبل وجود دارد.';
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * UI_REFERENCE_SOURCE: D-022 (parallel accounting books). Lists the tenant's
 * books — the statutory book («دفتر قانونی») and the management book
 * («دفتر مدیریتی») every tenant is seeded with, plus any added later — and lets
 * a user with `accounting.book.manage` add a book or close/reopen a
 * non-statutory one. The statutory book is the only one the taxpayer system
 * (Moadian) and VAT filing work on, which this page states plainly.
 */
export function BooksPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { hasCapability } = useCapabilities();
  const canManage = hasCapability('accounting.book.manage');
  const books = useApiGet<AccountingBookListResponse>(
    slug ? `${base}/api/v1/finance/books` : null,
    [slug],
  );

  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [kind, setKind] = useState<AccountingBook['kind']>('management');
  const [series, setSeries] = useState('');
  const [cutoverDate, setCutoverDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!slug) return null;

  function resetForm() {
    setName('');
    setCode('');
    setKind('management');
    setSeries('');
    setCutoverDate('');
    setError(null);
  }

  async function createBook(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiPost(`${base}/api/v1/finance/books`, {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        kind,
        ...(series.trim() ? { series: series.trim().toUpperCase() } : {}),
        ...(cutoverDate ? { cutoverDate } : {}),
      });
      setFormOpen(false);
      resetForm();
      books.reload();
      refreshFinanceBooks();
    } catch (err) {
      setError(errorText(err, 'ثبت دفتر ناموفق بود.'));
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(book: AccountingBook, status: 'active' | 'closed') {
    setBusy(true);
    setError(null);
    try {
      await apiPatch(`${base}/api/v1/finance/books/${book.bookId}`, { status });
      books.reload();
      refreshFinanceBooks();
    } catch (err) {
      setError(errorText(err, 'تغییر وضعیت دفتر ناموفق بود.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Alert tone="info" title="دفتر قانونی و دفتر مدیریتی">
        هر سازمان یک دفتر قانونی و یک دفتر مدیریتی دارد و گزارش‌های هر دفتر جداست. سامانه مودیان و
        اظهارنامهٔ ارزش افزوده فقط روی دفتر قانونی کار می‌کنند و برای دفتر مدیریتی غیرفعال‌اند.
        بقیهٔ امکانات مالی برای هر دو دفتر در دسترس است.
      </Alert>

      <V2Card>
        <V2CardHeader
          icon="finance"
          title="دفترهای حسابداری"
          subtitle="دفتر قانونی حذف یا غیرفعال نمی‌شود"
          actions={
            canManage ? (
              <V2Button
                type="button"
                variant="primary"
                size="sm"
                icon="plus"
                onClick={() => setFormOpen(true)}
              >
                دفتر جدید
              </V2Button>
            ) : null
          }
        />
        {error && !formOpen ? (
          <div style={{ padding: '0 20px 12px' }}>
            <Alert tone="danger" title="خطا">
              {error}
            </Alert>
          </div>
        ) : null}
        {books.status === 'loading' ? <LoadingState /> : null}
        {books.status === 'error' ? (
          <StatePanel
            variant={books.error.status === 403 ? 'permission_denied' : 'network_error'}
            title={books.error.status === 403 ? 'دسترسی ندارید' : 'بارگذاری دفترها ناموفق بود'}
          />
        ) : null}
        {books.status === 'ready' ? (
          <V2DataGrid
            columns={[
              {
                key: 'name',
                header: 'دفتر',
                render: (row: AccountingBook) => (
                  <span style={{ fontWeight: 600 }}>{row.name}</span>
                ),
              },
              {
                key: 'code',
                header: 'کد',
                render: (row: AccountingBook) => (
                  <span className="ltr" style={{ fontFamily: 'monospace' }}>
                    {row.code}
                  </span>
                ),
              },
              {
                key: 'kind',
                header: 'نوع',
                render: (row: AccountingBook) => (
                  <V2Badge tone={isStatutoryBook(row) ? 'info' : 'brand'}>
                    {KIND_LABEL[row.kind]}
                  </V2Badge>
                ),
              },
              {
                key: 'tax',
                header: 'مودیان و ارزش افزوده',
                render: (row: AccountingBook) =>
                  isStatutoryBook(row) ? (
                    <V2Badge tone="success">فعال</V2Badge>
                  ) : (
                    <V2Badge tone="neutral">غیرفعال</V2Badge>
                  ),
              },
              { key: 'series', header: 'سری شماره', render: (row: AccountingBook) => row.series },
              {
                key: 'cutover',
                header: 'تاریخ شروع',
                render: (row: AccountingBook) =>
                  row.cutoverDate ? displayJalaliDate(row.cutoverDate) : '—',
              },
              {
                key: 'status',
                header: 'وضعیت',
                render: (row: AccountingBook) => (
                  <V2Badge tone={row.status === 'active' ? 'success' : 'neutral'}>
                    {STATUS_LABEL[row.status]}
                  </V2Badge>
                ),
              },
              {
                key: 'actions',
                header: '',
                render: (row: AccountingBook) =>
                  canManage && !isStatutoryBook(row) && row.status !== 'archived' ? (
                    <V2Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        void setStatus(row, row.status === 'active' ? 'closed' : 'active')
                      }
                    >
                      {row.status === 'active' ? 'غیرفعال کردن' : 'فعال کردن'}
                    </V2Button>
                  ) : null,
              },
            ]}
            rows={books.data.items}
            rowKey={(row: AccountingBook) => row.bookId}
          />
        ) : null}
      </V2Card>

      <Modal
        open={formOpen}
        title="دفتر جدید"
        onClose={() => {
          if (busy) return;
          setFormOpen(false);
          resetForm();
        }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <V2Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setFormOpen(false);
                resetForm();
              }}
            >
              انصراف
            </V2Button>
            <V2Button type="submit" form="book-form" variant="primary" disabled={busy}>
              {busy ? 'در حال ثبت…' : 'ثبت دفتر'}
            </V2Button>
          </div>
        }
      >
        <form id="book-form" onSubmit={(e) => void createBook(e)}>
          <V2FormGrid>
            <V2FormField label="نام دفتر" required width="full">
              <input
                className="sk-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                required
              />
            </V2FormField>
            <V2FormField label="کد (انگلیسی)" required width="medium">
              <input
                className="sk-input ltr"
                dir="ltr"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                disabled={busy}
                maxLength={20}
                required
              />
            </V2FormField>
            <V2FormField label="نوع دفتر" required width="medium">
              <select
                className="sk-select"
                value={kind}
                onChange={(e) => setKind(e.target.value as AccountingBook['kind'])}
                disabled={busy}
              >
                {NEW_BOOK_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="سری شماره اسناد (اختیاری)" width="medium">
              <input
                className="sk-input ltr"
                dir="ltr"
                value={series}
                onChange={(e) => setSeries(e.target.value.toUpperCase())}
                disabled={busy}
                maxLength={10}
              />
            </V2FormField>
            <V2FormField label="تاریخ شروع (اختیاری)" width="medium">
              <JalaliDateInput value={cutoverDate} onChange={setCutoverDate} />
            </V2FormField>
          </V2FormGrid>
          {error ? (
            <div style={{ marginTop: 12 }}>
              <Alert tone="danger" title="خطا">
                {error}
              </Alert>
            </div>
          ) : null}
        </form>
      </Modal>
    </div>
  );
}
